/**
 * Admin Dashboard KPI chip helpers.
 *
 * Trend arrows (increase / decrease) are reserved for real period-over-period
 * deltas (attendance vs yesterday, headcount net). Queue cards use a status
 * chip: colour and copy describe the same signal, never a fake trend.
 */

/**
 * Chip for the Pending Leave Requests card.
 *
 * @param {number} pendingLeave - Submitted applications in the live queue
 * @returns {{ change: string, changeType: 'increase' | 'warning', showArrow: false }}
 */
export function getPendingLeaveChip(pendingLeave) {
  const pending = pendingLeave || 0;
  if (pending === 0) {
    return {
      change: 'Queue clear',
      changeType: 'increase',
      showArrow: false,
    };
  }
  return {
    change: 'Awaiting review',
    changeType: 'warning',
    showArrow: false,
  };
}

/**
 * Tooltip for the Pending Leave Requests card.
 *
 * @param {number} pendingLeave
 * @param {number | null | undefined} leaveApprovalRate - 30-day snapshot, not a delta
 * @returns {string}
 */
export function getPendingLeaveTooltip(pendingLeave, leaveApprovalRate) {
  const pending = pendingLeave || 0;
  const lines = [
    pending === 0
      ? 'No submitted applications waiting for a decision.'
      : 'Submitted applications waiting for a decision. Click to open the leave queue.',
  ];
  if (leaveApprovalRate != null) {
    lines.push(
      `${leaveApprovalRate}% of applications filed in the last 30 days are currently approved. This is a snapshot, not a change from the prior period.`
    );
  }
  return lines.join('\n\n');
}

/**
 * Tooltip for the Document Verification card.
 *
 * The count is the Documents inbox. Identity documents and family
 * certificates appear from upload. Education, professional, and bank
 * documents appear after the employee confirms extracted details.
 *
 * The card must land on the Dashboard worklist — there is no
 * `/admin/documents` page.
 */
export const DOCUMENT_VERIFICATION_QUEUE_HREF = '/dashboard#pending-verification';

/**
 * @param {number} pendingDocuments
 * @param {number | null | undefined} verifiedToday
 * @returns {string}
 */
export function getDocumentVerificationTooltip(pendingDocuments, verifiedToday) {
  const pending = pendingDocuments || 0;
  const lines = [
    pending === 0
      ? 'No documents waiting for HR verification.'
      : 'Documents waiting in the verification inbox. Click to open the queue.',
    'Identity documents and family certificates appear once uploaded. Education, professional, and bank documents appear after the employee confirms extracted details.',
  ];
  if (verifiedToday != null) {
    lines.push(`${verifiedToday} verified today.`);
  }
  return lines.join('\n\n');
}
